import { Box, Button, FormControl, FormErrorMessage, FormLabel, Heading, Image, Input, Link, SimpleGrid, Spinner, Stack, Text, Textarea, VStack, useToast, Grid, GridItem, useColorModeValue } from '@chakra-ui/react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { getSponsors, sendContact } from '../services/public';
import MainLayout from '../components/layout/MainLayout';
import { useForm } from 'react-hook-form';
import { assetUrl } from '../utils/url';
import { useTranslation } from 'react-i18next';

type ContactFormData = { name: string; email: string; subject: string; message: string; source?: string };

const SponsorsPage: React.FC = () => {
  const { t } = useTranslation();
  const { data, isLoading, isError, error } = useQuery({ queryKey: ['sponsors-page'], queryFn: getSponsors });
  const toast = useToast();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textSecondary = useColorModeValue('gray.600', 'gray.400');
  const textTertiary = useColorModeValue('gray.700', 'gray.300');

  const { register, handleSubmit, reset, formState: { errors } } = useForm<ContactFormData>();
  const { mutate, isLoading: sending } = useMutation({
    mutationFn: (payload: ContactFormData) => sendContact({ ...payload, source: 'sponsor' }),
    onSuccess: () => {
      reset();
      toast({ title: t('sponsors.message_sent'), description: t('sponsors.message_sent_desc'), status: 'success' });
    },
    onError: (e:any) => {
      const code = e?.code || '';
      const fromServer = e?.response?.data?.error || e?.response?.data?.message;
      const isTimeout = code === 'ECONNABORTED' || /timeout/i.test(String(e?.message || ''));
      const isNetwork = !!e?.isAxiosError && !e?.response;
      const msg = fromServer
        || (isTimeout ? t('sponsors.timeout_error')
            : isNetwork ? t('sponsors.network_error')
            : (e?.message || t('sponsors.send_error')));
      toast({ title: t('sponsors.error_title'), description: msg, status: 'error' });
    }
  });

  if (isLoading) return <Spinner />;
  if (isError) {
    const errMsg = (error as any)?.response?.data?.error || (error as any)?.message || t('sponsors.error_loading');
    return <Text color="red.500">{errMsg}</Text>;
  }

  // Group sponsors by tier and sort within groups
  const sorter = (a: any, b: any) => {
    const ao = (a?.display_order ?? 9999);
    const bo = (b?.display_order ?? 9999);
    if (ao !== bo) return ao - bo;
    return String(a?.name || '').localeCompare(String(b?.name || ''));
  };
  const generalPartners = (data?.filter((s) => s.tier === 'general') || []).slice().sort(sorter);
  const standardSponsors = (data?.filter((s) => s.tier !== 'general') || []).slice().sort(sorter);

  return (
    <MainLayout>
      <VStack align="stretch" spacing={10} maxW="7xl" mx="auto" px={{ base: 4, md: 6 }} py={{ base: 6, md: 10 }}>
        {/* Main sponsors section */}
        <Box>
          <Heading size="lg" mb={6}>{t('sponsors.title')}</Heading>
          
          {/* General Partners - Larger display */}
          {generalPartners.length > 0 && (
            <Box mb={8}>
              <Heading size="md" mb={4} color="brand.600">{t('sponsors.main_partners')}</Heading>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
                {generalPartners.map((s) => (
                  <Stack key={s.id} align="center" bg={cardBg} p={6} borderRadius="lg" borderWidth="2px" borderColor={borderColor} boxShadow="md">
                    <Link href={s.website_url || '#'} isExternal>
                      <Image src={assetUrl(s.logo_url) || '/sponsor-placeholder.svg'} alt={s.name} height="100px" objectFit="contain" />
                    </Link>
                    <Text fontWeight="semibold" fontSize="lg">{s.name}</Text>
                  </Stack>
                ))}
              </SimpleGrid>
            </Box>
          )}

          {/* Standard Sponsors - Regular display */}
          {standardSponsors.length > 0 && (
            <Box>
              <Heading size="md" mb={4} color={textTertiary}>{t('sponsors.partners')}</Heading>
              <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} spacing={6}>
                {standardSponsors.map((s) => (
                  <Stack key={s.id} align="center" bg={cardBg} p={4} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                    <Link href={s.website_url || '#'} isExternal>
                      <Image src={assetUrl(s.logo_url) || '/sponsor-placeholder.svg'} alt={s.name} height="60px" objectFit="contain" />
                    </Link>
                    <Text fontSize="sm">{s.name}</Text>
                  </Stack>
                ))}
              </SimpleGrid>
            </Box>
          )}
        </Box>

        {/* Kontakt pro nové partnery – stylově sladěný s hlavní kontaktní stránkou */}
        <Box maxW="600px" mx="auto" w="full" alignSelf="stretch">
          <Box mb={4}>
            <Heading size="lg" mb={2}>
              {t('sponsors.become_partner')}
            </Heading>
            <Text color={textSecondary}>
              {t('sponsors.partnership_description')}
            </Text>
          </Box>

          <Box
            bg={cardBg}
            borderRadius="lg"
            borderWidth="1px"
            borderColor={borderColor}
            boxShadow="sm"
            p={6}
          >
            <form onSubmit={handleSubmit((vals) => mutate({ ...vals }))}>
              <VStack spacing={4}>
                <FormControl isInvalid={!!errors.name}>
                  <FormLabel htmlFor="partner-name">{t('sponsors.name_label')}</FormLabel>
                  <Input
                    id="partner-name"
                    placeholder={t('sponsors.name_placeholder')}
                    {...register('name', { required: t('sponsors.name_required') })}
                  />
                  <FormErrorMessage>
                    {errors.name?.message}
                  </FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.email}>
                  <FormLabel htmlFor="partner-email">{t('sponsors.email_label')}</FormLabel>
                  <Input
                    id="partner-email"
                    type="email"
                    placeholder={t('sponsors.email_placeholder')}
                    {...register('email', { required: t('sponsors.email_required') })}
                  />
                  <FormErrorMessage>
                    {errors.email?.message}
                  </FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.subject}>
                  <FormLabel htmlFor="partner-subject">{t('sponsors.subject_label')}</FormLabel>
                  <Input
                    id="partner-subject"
                    placeholder={t('sponsors.subject_placeholder')}
                    {...register('subject', { required: t('sponsors.subject_required') })}
                  />
                  <FormErrorMessage>
                    {errors.subject?.message}
                  </FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.message}>
                  <FormLabel htmlFor="partner-message">{t('sponsors.message_label')}</FormLabel>
                  <Textarea
                    id="partner-message"
                    rows={6}
                    placeholder={t('sponsors.message_placeholder')}
                    {...register('message', { required: t('sponsors.message_required') })}
                  />
                  <FormErrorMessage>
                    {errors.message?.message}
                  </FormErrorMessage>
                </FormControl>

                <Button
                  type="submit"
                  colorScheme="brand"
                  size="lg"
                  width="full"
                  mt={2}
                  isLoading={sending}
                  loadingText={t('sponsors.sending')}
                >
                  {t('sponsors.send_message')}
                </Button>
              </VStack>
            </form>
          </Box>
        </Box>
      </VStack>
    </MainLayout>
  );
}

export default SponsorsPage;
